/**
 * A small CSV reader and writer.
 *
 * Written here rather than pulled in as a dependency because the need is narrow and the
 * bundle is already large. It handles the parts that actually bite in spreadsheet exports:
 * quoted fields, embedded commas, newlines inside quotes, and doubled quotes.
 */

/** Excel assumes the system codepage without this, which mangles non ASCII names. */
export const UTF8_BOM = '\ufeff';

export function parseCsv(text: string): string[][] {
	let rows: string[][] = [];

	if (!text) {
		return rows;
	}

	// Strip the byte order mark so the first header does not carry an invisible character.
	let input = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;

	let row: string[] = [];
	let value = '';
	let inQuotes = false;
	let index = 0;

	function endValue() {
		row.push(value);
		value = '';
	}

	function endRow() {
		endValue();
		// A trailing newline would otherwise add an empty row.
		if (row.length > 1 || row[0] !== '') {
			rows.push(row);
		}
		row = [];
	}

	while (index < input.length) {
		let char = input[index];

		if (inQuotes) {
			if (char === '"') {
				if (input[index + 1] === '"') {
					value += '"';
					index += 2;
					continue;
				}
				inQuotes = false;
				index++;
				continue;
			}

			value += char;
			index++;
			continue;
		}

		if (char === '"') {
			inQuotes = true;
			index++;
			continue;
		}

		if (char === ',') {
			endValue();
			index++;
			continue;
		}

		if (char === '\r') {
			// Treat CRLF and lone CR the same as LF.
			if (input[index + 1] === '\n') {
				index++;
			}
			endRow();
			index++;
			continue;
		}

		if (char === '\n') {
			endRow();
			index++;
			continue;
		}

		value += char;
		index++;
	}

	if (value !== '' || row.length) {
		endRow();
	}

	return rows;
}

function escapeCsvValue(value: any): string {
	if (value === null || value === undefined) {
		return '';
	}

	let text = String(value);

	if (/[",\r\n]/.test(text)) {
		return `"${text.replace(/"/g, '""')}"`;
	}

	// A leading space or a value Excel would reinterpret is safer quoted.
	return text;
}

export function toCsv(rows: any[][]): string {
	return rows.map((row) => row.map(escapeCsvValue).join(',')).join('\r\n');
}

/** Turns a parsed sheet into objects keyed by its header row. */
export function rowsToObjects(rows: string[][]): { headers: string[]; records: { [key: string]: string }[] } {
	if (!rows.length) {
		return { headers: [], records: [] };
	}

	let headers = rows[0].map((header) => header.trim());

	let records = rows.slice(1).map((row) => {
		let record: { [key: string]: string } = {};

		headers.forEach((header, index) => {
			record[header] = (row[index] ?? '').trim();
		});

		return record;
	});

	return { headers, records };
}
